/**
 * Corta o texto que chega token a token em FRASES prontas pra sintetizar em
 * voz — é o que permite o Modo de Voz falar "conforme escreve" (04/09) em
 * vez de esperar a resposta inteira terminar.
 *
 * Puro e sem estado próprio: quem chama guarda o `bufferPendente` entre
 * chamadas (mesmo padrão de `utils/fase-do-turno.js` — função pura + estado
 * do lado de fora). Sem lookbehind (`(?<=...)`) de propósito — o Babel deste
 * repo não garante suporte, mesma cautela do resto do módulo com sintaxe
 * nova de regex/JS.
 *
 * Heurística deliberadamente simples (v1): corta em `.`/`!`/`?` seguido de
 * espaço/quebra de linha/fim, ou em linha em branco (`\n\n`) — cobre parágrafo
 * e título de markdown (`### Título`) sem pontuação final. NÃO tenta detectar
 * abreviação ("Sr.", "3.14") nem markdown/HTML embutido no texto (a mesma
 * síntese "manda o texto cru" que já roda hoje na síntese não-progressiva) —
 * over-engineering pra um corte de frase que só precisa soar natural na
 * maioria dos casos, não ser gramaticalmente perfeito.
 */

const FECHAMENTOS = '.!?"\')]'
const TERMINADORES_DE_FRASE = '.!?'

/** `\n\n` a partir de `i` — devolve o índice logo depois dela, ou `null`. */
const acharFimDeQuebraDeParagrafo = (acumulado, i) => {
  if (acumulado[i] !== '\n' || acumulado[i + 1] !== '\n') return null
  return i + 2
}

/**
 * `.`/`!`/`?` (+ fechamentos como aspas/parênteses logo depois) a partir de
 * `i`, só quando um SEPARADOR DE VERDADE confirma que a frase terminou
 * (espaço ou quebra de linha) — devolve o índice logo depois do separador
 * (já consumindo ele, pra não sobrar sozinho no buffer pendente), ou `null`.
 *
 * ⚠️ Fim do buffer JÁ RECEBIDO (`undefined`) NÃO conta como confirmação —
 * é o texto ainda estar chegando aos pedaços via SSE, não o fim da frase:
 * "Sério?! Que isso..." com o delta cortando bem em cima das reticências
 * pareceria "frase terminada" ali, quando na verdade é só o token seguinte
 * que ainda não chegou. A frase certa fica pendente até um separador
 * confirmar, ou até `fecharBufferRestante` no fim do turno.
 */
const acharFimDePontuacaoFinal = (acumulado, i) => {
  if (TERMINADORES_DE_FRASE.indexOf(acumulado[i]) === -1) return null
  let fim = i + 1
  while (fim < acumulado.length && FECHAMENTOS.indexOf(acumulado[fim]) !== -1) {
    fim += 1
  }
  const proximoCaractere = acumulado[fim]
  if (proximoCaractere !== ' ' && proximoCaractere !== '\n') return null
  return fim + 1
}

/**
 * Avança UM passo a partir de `i`: se achar um corte (parágrafo ou
 * pontuação final), fecha a frase de `inicio` até lá e some com ela em
 * `frases`; senão, só anda uma posição. Devolve pra onde `inicio`/`i` vão a
 * seguir — é o corpo do laço de `avancarSegmentacao`, extraído pra manter
 * a função de fora pequena (o laço em si não decide nada, só repete isto).
 */
const processarProximoCorte = (acumulado, i, inicio, frases) => {
  const fimDeQuebra = acharFimDeQuebraDeParagrafo(acumulado, i)
  const fim = fimDeQuebra !== null ? fimDeQuebra : acharFimDePontuacaoFinal(acumulado, i)
  if (fim === null) return { proximoInicio: inicio, proximoI: i + 1 }

  // Quebra de parágrafo corta ANTES do `\n\n`; pontuação final inclui o
  // próprio terminador (e fechamentos) na frase.
  const corteDaFrase = fimDeQuebra !== null ? i : fim
  const frase = acumulado.slice(inicio, corteDaFrase).trim()
  if (frase) frases.push(frase)
  return { proximoInicio: fim, proximoI: fim }
}

/**
 * @param {string} bufferPendente Texto ainda não cortado em frase, de chamadas anteriores.
 * @param {string} textoNovo Delta novo chegando agora (pode ser vazio).
 * @returns {{frases: string[], bufferPendente: string}}
 */
export const avancarSegmentacao = (bufferPendente, textoNovo) => {
  const acumulado = String(bufferPendente || '') + String(textoNovo || '')
  const frases = []
  let inicio = 0
  let i = 0

  while (i < acumulado.length) {
    const passo = processarProximoCorte(acumulado, i, inicio, frases)
    inicio = passo.proximoInicio
    i = passo.proximoI
  }

  return { frases, bufferPendente: acumulado.slice(inicio) }
}

/**
 * Fecha o que sobrou no buffer como uma frase final — chamado no fim do
 * turno (`end`), pra nenhum trecho sem pontuação de fechamento (ex.: título
 * de markdown na última linha) ficar sem ser falado.
 *
 * @param {string} bufferPendente
 * @returns {string[]} `[]` quando não sobra nada de fato (só espaço/vazio).
 */
export const fecharBufferRestante = (bufferPendente) => {
  const frase = String(bufferPendente || '').trim()
  return frase ? [frase] : []
}

/**
 * [V3, voz v3, 08/09] Tira a marcação Markdown mais comum de uma frase antes
 * de mandá-la pro TTS. Usado só no FALLBACK de `finalizarFalaProgressiva`
 * (`motor-de-voz.js`/`fala-da-ia.js`) — quando o turno termina sem o evento
 * SSE `fala` (a versão falada que o backend gera), a fala sai do texto
 * ESCRITO, que pode trazer `#`/`**`/`` ` ``/links; sem isso a IA "lê" os
 * símbolos em voz alta. Deliberadamente simples (v1): não é um parser de
 * Markdown, só limpa os símbolos mais comuns que aparecem em respostas do
 * chat — mesma cautela de escopo do resto deste arquivo.
 *
 * @param {string} texto
 * @returns {string}
 */
export const removerMarkdownSimples = (texto) => {
  return String(texto || '')
    .replace(/\[([^\]]*)\]\([^)]*\)/g, '$1') // [texto](url) -> texto
    .replace(/`{1,3}/g, '') // backticks (inline ou code fence)
    .replace(/[*_#>]+/g, '') // ênfase, títulos, citação
    .replace(/^-{1,3}\s*/gm, '') // marcador de lista
    .replace(/\s{2,}/g, ' ')
    .trim()
}
